import React, { useMemo } from 'react';
import { Box } from '@mui/material';

interface HighlightedTextProps {
  content: string;
  query: string;
}

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Read-only, scrollable rendering of plain text with case-insensitive
 * matches of `query` wrapped in <mark>. Used when an in-output search is
 * active (a MUI TextField cannot highlight its own contents).
 */
const HighlightedText: React.FC<HighlightedTextProps> = ({ content, query }) => {
  const segments = useMemo(() => {
    if (!query) return [content];
    const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
    return content.split(regex);
  }, [content, query]);

  return (
    <Box
      sx={{
        p: 1.5,
        minHeight: 320,
        maxHeight: 640,
        overflow: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        fontFamily: 'monospace',
        fontSize: '0.9rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        '& mark': {
          backgroundColor: 'warning.main',
          color: '#000',
          borderRadius: '2px',
          px: '1px',
        },
      }}
    >
      {segments.map((segment, index) =>
        index % 2 === 1 ? <mark key={index}>{segment}</mark> : <React.Fragment key={index}>{segment}</React.Fragment>
      )}
    </Box>
  );
};

export default HighlightedText;
